import React from 'react';
import PropTypes from 'prop-types';

/**
 * Minimal SVG sparkline — no dependencies, no tooltip.
 * Pure trend indicator under a big metric number.
 *
 * Props:
 *   values     - number[]  — data points, newest last
 *   color      - string    — stroke colour (default SAG green)
 *   fillOpacity - number    — area fill opacity (default 0.15)
 *   height     - number    — pixel height (default 24)
 *   width      - number    — pixel width (default 100% of container)
 */
function Sparkline({ values, color, fillOpacity, height, width }) {
    if (!Array.isArray(values) || values.length === 0) return null;

    const w = width;
    const h = height;
    const pad = 2;

    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const range = max - min || 1;

    // Build points
    const stepX = values.length > 1 ? (w - pad * 2) / (values.length - 1) : 0;
    const points = values.map((v, i) => {
        const x = pad + i * stepX;
        const y = h - pad - ((v - min) / range) * (h - pad * 2);
        return [x, y];
    });

    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
    const areaPath = `${linePath} L ${points[points.length - 1][0].toFixed(1)} ${h - pad} L ${points[0][0].toFixed(1)} ${h - pad} Z`;

    return (
        <svg
            width="100%"
            height={h}
            viewBox={`0 0 ${w} ${h}`}
            preserveAspectRatio="none"
            className="sparkline"
            aria-hidden="true"
        >
            <path d={areaPath} fill={color} fillOpacity={fillOpacity} />
            <path d={linePath} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

Sparkline.propTypes = {
    values: PropTypes.arrayOf(PropTypes.number).isRequired,
    color: PropTypes.string,
    fillOpacity: PropTypes.number,
    height: PropTypes.number,
    width: PropTypes.number,
};

Sparkline.defaultProps = {
    color: '#6f8f5c',
    fillOpacity: 0.18,
    height: 24,
    width: 120,
};

export default Sparkline;
